import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import type { Ingestion, Atom, Concept } from '@/types'

export function useIngestions(limit = 20) {
  const dependencies = [limit]
  
  return useSupabase<Ingestion[]>(async () => {
    const { data, error } = await supabase
      .from('ingestions')
      .select(`
        *,
        primary_domain:domains!ingestions_primary_domain_id_fkey(*)
      `)
      .order('ingested_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies)
}

export function useIngestionById(id: string | undefined) {
  const dependencies = [id]
  
  return useSupabase<Ingestion | null>(async () => {
    if (!id) return null
    
    const { data, error } = await supabase
      .from('ingestions')
      .select(`
        *,
        primary_domain:domains!ingestions_primary_domain_id_fkey(*)
      `)
      .eq('id', id)
      .maybeSingle()

    // maybeSingle returns null if not found, only throw on real errors
    if (error && error.code !== 'PGRST116') throw error
    return data
  }, dependencies, !!id)
}

export function useRecentIngestions(limit = 5) {
  const dependencies = [limit]
  
  return useSupabase<Ingestion[]>(async () => {
    const { data, error } = await supabase
      .from('ingestions')
      .select(`
        *,
        primary_domain:domains!ingestions_primary_domain_id_fkey(*)
      `)
      .eq('status', 'completed')
      .order('processed_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies)
}

export function useIngestionsByDomain(domainId: string | undefined) {
  const dependencies = [domainId]
  
  return useSupabase<Ingestion[]>(async () => {
    if (!domainId) return []
    
    const { data, error } = await supabase
      .from('ingestions')
      .select(`
        *,
        primary_domain:domains!ingestions_primary_domain_id_fkey(*)
      `)
      .eq('primary_domain_id', domainId)
      .order('ingested_at', { ascending: false })

    if (error) throw error
    return data || []
  }, dependencies, !!domainId)
}

export function useIngestionAtoms(ingestionId: string | undefined) {
  const dependencies = [ingestionId]
  
  return useSupabase<Atom[]>(async () => {
    if (!ingestionId) return []

    const { data, error } = await supabase
      .from('atoms')
      .select('*')
      .eq('ingestion_id', ingestionId)
      .order('created_at')

    if (error) throw error
    return data || []
  }, dependencies, !!ingestionId)
}

export function useIngestionConcepts(ingestionId: string | undefined) {
  const dependencies = [ingestionId]
  
  return useSupabase<Concept[]>(async () => {
    if (!ingestionId) return []

    const { data, error } = await supabase
      .from('concepts')
      .select(`
        *,
        domain:domains(*)
      `)
      .eq('ingestion_id', ingestionId)
      .order('synapse_strength', { ascending: false })

    if (error) throw error
    return data || []
  }, dependencies, !!ingestionId)
}

export function useIngestionStats() {
  return useSupabase<{
    total: number
    by_status: Record<string, number>
    by_content_type: Record<string, number>
    recent_count: number
  }>(async () => {
    // Get total count
    const { count: total } = await supabase
      .from('ingestions')
      .select('id', { count: 'exact' })

    // Get by status
    const { data: statusData } = await supabase
      .from('ingestions')
      .select('status')

    const by_status = statusData?.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1
      return acc
    }, {} as Record<string, number>) || {}

    // Get by content type
    const { data: contentTypeData } = await supabase
      .from('ingestions')
      .select('content_type')

    const by_content_type = contentTypeData?.reduce((acc, item) => {
      acc[item.content_type] = (acc[item.content_type] || 0) + 1
      return acc
    }, {} as Record<string, number>) || {}

    // Get recent count (last 7 days)
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    
    const { count: recent_count } = await supabase
      .from('ingestions')
      .select('id', { count: 'exact' })
      .gte('ingested_at', sevenDaysAgo.toISOString())

    return {
      total: total || 0,
      by_status,
      by_content_type,
      recent_count: recent_count || 0
    }
  })
}